import { useMemo } from 'react';

import { EventStoreItem } from '@givebacks/api/store';
import { Native } from '@givebacks/utils';
import { AdvancedMarker, usePlatformService, useUserLocation } from 'core';
import { Box, MapMarker, Stack, Text, UnstyledButton } from 'ui';

import classes from './EventLocation.module.css';

const EventLocation = ({
  eventLocation,
  EventMap,
}: {
  eventLocation: EventStoreItem['location'];
  EventMap?: React.JSXElementConstructor<Record<string, unknown>>;
}) => {
  const platform = usePlatformService();
  const { geolocation } = useUserLocation();

  const address = useMemo(
    () =>
      eventLocation && Object.keys(eventLocation).length
        ? `${eventLocation?.address1 ?? ''} ${eventLocation?.address2 ?? ''} ${
            eventLocation?.city ? eventLocation.city + ',' : ''
          } ${eventLocation?.state ?? ''} ${eventLocation?.zip_code ?? ''}`
        : null,
    [eventLocation]
  );

  const lat = eventLocation?.latitude;
  const lng = eventLocation?.longitude;
  const encodedAddress = encodeURIComponent(address || '');

  return (
    <Stack gap="md">
      {(!!eventLocation?.venue_name || !!address) && (
        <Stack gap="0">
          {!!eventLocation?.venue_name && (
            <Text className={classes.EventLocation__venueName}>{eventLocation.venue_name}</Text>
          )}

          {!!address && (
            <UnstyledButton
              c="cobalt"
              fz="sm"
              onClick={() => {
                if (platform.isNative) {
                  return Native.postAction('directions', {
                    userLocation: {
                      latitude: geolocation?.latitude,
                      longitude: geolocation?.longitude,
                    },
                    desiredLocation: {
                      latitude: lat,
                      longitude: lng,
                    },
                  });
                }

                if (geolocation?.latitude && geolocation?.longitude) {
                  window.open(
                    `https://www.google.com/maps/dir/${geolocation?.latitude},${geolocation?.longitude}/${lat},${lng}`,
                    '_blank'
                  );
                } else if (eventLocation?.address1 && encodedAddress) {
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
                    '_blank'
                  );
                } else {
                  window.open(`https://www.google.com/maps/place/${lat},+${lng}`, '_blank');
                }
              }}
            >
              {address}
            </UnstyledButton>
          )}
        </Stack>
      )}

      {!!EventMap && !!lat && !!lng && (
        <Box className={classes.EventLocation__mapContainer}>
          <EventMap
            defaultLocation={{
              lat,
              lng,
            }}
          >
            <AdvancedMarker
              position={{
                lat,
                lng,
              }}
            >
              <MapMarker />
            </AdvancedMarker>
          </EventMap>
        </Box>
      )}
    </Stack>
  );
};

export { EventLocation };
